// src/routes/ProtectedRoute.tsx
import React, { useEffect, useRef, useState } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/auth/hooks';
import { useSubscription } from '../hooks/useSubscription';
import { clearAuthCache, logCacheState } from '../lib/authCache';
import toast from 'react-hot-toast';

// טיימרים/חסד
const BOOT_POLL_MS = 1200;           // פחות אגרסיבי
const BOOT_HARD_TIMEOUT_MS = 10000;  // 10 שניות - יותר זמן להבין שאין session
const BOOT_GRACE_TTL_MS = 120000;    // 2 דקות חסד
const BOOT_GRACE_KEY = 'lf_boot_grace_until';
const SESSION_CHECK_TIMEOUT = 10000; // 10 שניות timeout לבדיקת session

// helper: חסד
function hasBootGrace() {
  const raw = localStorage.getItem(BOOT_GRACE_KEY);
  const ts = raw ? parseInt(raw, 10) : 0;
  return Number.isFinite(ts) && Date.now() < ts;
}
function setBootGrace(ttlMs: number) {
  localStorage.setItem(BOOT_GRACE_KEY, String(Date.now() + ttlMs));
}

// helper: האם יש לנו צילום מצב (snapshot) שמצביע שהיה business בעבר
function hasBusinessSnapshot() {
  const businessId = localStorage.getItem('businessId');
  if (businessId && businessId.trim()) return true;

  // אם אתה שומר גם אובייקט ב־localStorage (אופציונלי):
  try {
    const raw = localStorage.getItem('lf_business_cache');
    if (!raw) return false;
    const obj = JSON.parse(raw);
    return !!obj?.id;
  } catch {
    return false;
  }
}

// מסך ביניים מינימלי (לא מסך מלא) – נראה רק כשאין חסד/צילום מצב
function BootInline() {
  return (
    <div dir="rtl" className="w-full py-6 flex items-center justify-center">
      <div className="inline-flex items-center gap-3 text-gray-500 text-sm">
        <span className="animate-pulse h-2 w-2 rounded-full bg-gray-400" />
        טוען נתוני עסק…
      </div>
    </div>
  );
}

// שער הקמה מצומצם: מפעיל refresh פעם ב־~1.2ש', מקצה חסד אחרי 10ש' וממשיך
// אם אין session אחרי ה-timeout - מנקה ומנווט ל-login
function BootGate() {
  const { user, business, refreshBusiness } = useAuth();
  const navigate = useNavigate();
  const pollRef = useRef<number | null>(null);
  const hardRef = useRef<number | null>(null);
  const [showActions, setShowActions] = useState(false);
  const [attemptCount, setAttemptCount] = useState(0);

  useEffect(() => {
    let cancelled = false;

    const poll = async () => {
      if (cancelled) return;

      console.log('[BootGate] Polling... user:', !!user, 'business:', !!business, 'attempt:', attemptCount);

      // אם אין user - זה אומר שה-session פג ולא התחדש
      if (!user) {
        console.error('[BootGate] No user found during poll - session expired!');
        console.log('[BootGate] Clearing cache and redirecting to login...');
        logCacheState();
        clearAuthCache();
        toast.error('תוקף ההתחברות פג. יש להתחבר מחדש.');
        navigate('/login', { replace: true });
        return;
      }

      try {
        await refreshBusiness(user);
        setAttemptCount(prev => prev + 1);
      } catch (err) {
        console.error('[BootGate] refreshBusiness failed:', err);
      }

      if (!business) {
        pollRef.current = window.setTimeout(poll, BOOT_POLL_MS);
      }
    };

    poll();

    // Timeout: אחרי 10 שניות, אם עדיין אין business, בדוק אם יש user
    hardRef.current = window.setTimeout(() => {
      if (cancelled) return;

      console.log('[BootGate] Timeout reached. user:', !!user, 'business:', !!business);

      if (!user) {
        // אין session! נקה ונווט
        console.error('[BootGate] Timeout: No user session found!');
        logCacheState();
        clearAuthCache();
        toast.error('תוקף ההתחברות פג. יש להתחבר מחדש.');
        navigate('/login', { replace: true });
        return;
      }

      if (!business) {
        // יש user אבל אין business - תן חסד וממשיך
        console.warn('[BootGate] Timeout: User exists but no business found, granting grace period');
        setBootGrace(BOOT_GRACE_TTL_MS);
        setShowActions(true); // הצג כפתורי פעולה
      }
    }, BOOT_HARD_TIMEOUT_MS);

    return () => {
      cancelled = true;
      if (pollRef.current) window.clearTimeout(pollRef.current);
      if (hardRef.current) window.clearTimeout(hardRef.current);
    };
  }, [user, business, refreshBusiness, navigate, attemptCount]);

  // מסך ביניים קטן עם אופציה לכפתורים אחרי timeout
  if (showActions) {
    return (
      <div dir="rtl" className="w-full py-6 flex flex-col items-center justify-center gap-4">
        <div className="inline-flex items-center gap-3 text-gray-500 text-sm">
          <span className="animate-pulse h-2 w-2 rounded-full bg-gray-400" />
          טוען נתוני עסק…
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => window.location.reload()}
            className="px-4 py-2 bg-indigo-600 text-white rounded-lg text-sm hover:bg-indigo-700 transition-colors"
          >
            רענן דף
          </button>
          <button
            onClick={() => {
              clearAuthCache();
              navigate('/login', { replace: true });
            }}
            className="px-4 py-2 bg-gray-600 text-white rounded-lg text-sm hover:bg-gray-700 transition-colors"
          >
            התחבר מחדש
          </button>
        </div>
      </div>
    );
  }

  return <BootInline />;
}

export default function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { user, loading, hasAppUser, business, subscriptionStatus } = useAuth();
  const { subscription, hasSubscription, loading: subscriptionLoading } = useSubscription();
  const location = useLocation();
  const navigate = useNavigate();
  const sessionCheckRef = useRef<number | null>(null);
  const [subscriptionGracePassed, setSubscriptionGracePassed] = useState(false);

  const pathname = location.pathname || '';
  const isRegistration = pathname.startsWith('/register') || pathname.startsWith('/registration');
  const isAuthCallback = pathname.startsWith('/auth/callback');
  const isPlanUpgrade = pathname.startsWith('/plan-upgrade');
  const isSubscriptionManagement = pathname.startsWith('/subscription');
  const isOnboardingComplete = pathname.startsWith('/welcome/onboarding-complete');
  const isSettings = pathname.startsWith('/settings');
  const isPublicAllowed =
    isRegistration || isAuthCallback || isPlanUpgrade || isSubscriptionManagement || isOnboardingComplete;

  // Grace period למנוי: תן 3 שניות לפני שבודקים מנוי
  useEffect(() => {
    const timer = setTimeout(() => {
      setSubscriptionGracePassed(true);
    }, 3000);

    return () => clearTimeout(timer);
  }, []);

  // Session Validation: אם הטעינה נגמרה ואין user, אבל יש localStorage ישן - נקה ונווט
  useEffect(() => {
    if (!loading && !user && !isPublicAllowed) {
      // בדוק אם יש cache ישן
      if (hasBusinessSnapshot()) {
        console.warn('[ProtectedRoute] Loading finished, no user, but stale localStorage detected!');
        logCacheState();

        // תן timeout קטן למקרה שהסשן בדיוק מתחדש
        sessionCheckRef.current = window.setTimeout(() => {
          if (!user) {
            console.error('[ProtectedRoute] Session check timeout - no session found after loading');
            console.log('[ProtectedRoute] Clearing stale cache and redirecting to login...');
            clearAuthCache();
            toast.error('תוקף ההתחברות פג. יש להתחבר מחדש.');
            navigate('/login', { replace: true });
          }
        }, SESSION_CHECK_TIMEOUT);
      }
    }

    return () => {
      if (sessionCheckRef.current) {
        window.clearTimeout(sessionCheckRef.current);
      }
    };
  }, [loading, user, isPublicAllowed, navigate]);

  // 1) אם עדיין טוענים auth/appUser:
  //    במקום מסך מלא – אם יש חסד/צילום מצב → רנדר מיד; אחרת, אינליין קטן.
  if (loading || hasAppUser === null) {
    // בדיקה נוספת: אם הטעינה נמשכת יותר מדי והיא לא כוללת user - נקה
    if (!loading && !user && hasBusinessSnapshot()) {
      console.warn('[ProtectedRoute] Detected stale cache during loading phase');
      clearAuthCache();
      return <Navigate to="/login" replace />;
    }

    if (hasBootGrace() || hasBusinessSnapshot()) {
      return <>{children}</>;
    }
    return <BootInline />;
  }

  // 2) לא מחובר: מותר רק מסכים ציבוריים
  if (!user && !isPublicAllowed) {
    // נקה localStorage לפני ניווט
    if (hasBusinessSnapshot()) {
      console.log('[ProtectedRoute] No user and not public page, clearing cache');
      clearAuthCache();
    }
    return <Navigate to="/login" replace />;
  }

  // 3) יש סשן אבל אין app user → להרשמה
  if (user && hasAppUser === false && !isRegistration) {
    return <Navigate to="/register/business" replace />;
  }

  // 4) יש app user אבל אין business עדיין:
  //    אם יש חסד/צילום מצב – ממשיכים מיד (אופטימי). אחרת מפעילים BootGate המצומצם.
  if (user && hasAppUser === true && !business && !isRegistration) {
    if (hasBootGrace() || hasBusinessSnapshot()) {
      return <>{children}</>;
    }
    return <BootGate />;
  }

  // 5) בדיקת מנוי (רק במסכים מוגנים)
  // המתן לגרייס פיריוד לפני בדיקה, ובדוק גם את hasSubscription וגם את subscription
  if (
    user &&
    hasAppUser === true &&
    business &&
    !isPublicAllowed &&
    !subscriptionLoading &&
    subscriptionGracePassed
  ) {
    // בדוק אם יש מנוי באמצעות useSubscription - בדיקה כפולה לוודא שאין false positive
    if (!hasSubscription && !subscription) {
      console.log('[ProtectedRoute] No subscription detected after grace period', {
        hasSubscription,
        subscription,
        subscriptionLoading,
        subscriptionGracePassed
      });

      // אם אנחנו ב-/settings - אפשר רק גישה חלקית (נבדוק בקומפוננט עצמו)
      if (!isSettings) {
        return <Navigate to="/plan-upgrade" replace />;
      }
    }
    // בדיקת סטטוס מנוי (fallback למי שיש מנוי אבל לא פעיל)
    if (subscriptionStatus) {
      if (!subscriptionStatus.is_active || subscriptionStatus.is_expired) {
        if (!isSettings) {
          return <Navigate to="/plan-upgrade" replace />;
        }
      }
    }
  }

  // 6) הכל ירוק
  return <>{children}</>;
}
